import styled from "styled-components/macro";
import { focusOutline, focusVisible, focusVisibleInset } from "../../../styles/a11y";
import { themeVariables } from "../../../themeVariables";

export const StyledLeftBarContainer = styled.div`
  position: relative;
  hr {
    border-top: 1px solid var(--divider);
    border-style: solid;
    border-bottom: 0px;
    width: 100%;
  }

  .sr-only {
    position: absolute;
    width: 1px;
    height: 1px;
    margin: -1px;
    overflow: hidden;
    clip: rect(0, 0, 0, 0);
    white-space: nowrap; /* keeps the text on one line */
  }

  &.sw2 {
    --leftbar-create-button-max: 111px;
    --leftbar-create-button-min: 54px;
    --leftbar-create-button-gap: 8px;
    --leftbar-chevron-inline-space: 56px;
    --leftbar-collapse-top: 17px;

    display: flex;
    flex-direction: column;
    flex-wrap: nowrap;
    height: 100%;

    &.compact {
      --leftbar-collapse-top: 15px;

      .add {
        min-width: var(--leftbar-create-button-min);
        max-width: var(--leftbar-create-button-max);
        width: auto;
        height: 33.6px;
        display: flex;
        align-items: center;
        justify-content: center;
        flex: 1 1 var(--leftbar-create-button-min);
        box-sizing: border-box;
        padding: 2px 10px;

        p {
          display: none;
        }

        svg {
          flex-shrink: 0;
        }
      }
    }

    .top-menu,
    .create-section,
    .story-bible-menu,
    .canvas-menu-container,
    .bottom-links {
      flex-grow: 0;
      flex-shrink: 0;
    }

    .canvas-menu-container {
      .inner-container {
        padding: 0;
        border-radius: 20px;
      }
      a {
        padding: 14px 12px;
      }
    }

    .create-section {
      box-sizing: border-box;
      gap: var(--leftbar-create-button-gap);
      min-width: 0;
      padding: 10px var(--leftbar-chevron-inline-space) 12px 16px;
    }

    .top-menu {
      border-bottom: none;
      height: 0;
      z-index: 2;

      .collapse {
        z-index: 1;
        top: var(--leftbar-collapse-top);
      }
    }

    &.separate-collapse-row {
      --leftbar-collapse-top: 10px;

      .create-section {
        padding-right: 16px;
      }

      .top-menu {
        height: 44px;
      }
    }

    .trashed {
      h3 {
        font-family: Inter;
        font-weight: 600;
        text-transform: uppercase;
        margin-left: 0px;
        font-size: 14px;
        color: var(--grey-2);
        letter-spacing: 1px;
        padding: 8px 0px 4px 0px;
      }
      a {
        color: var(--grey-0);
        flex-grow: 1;
      }
      .empty-trash {
        margin-left: 0px;
        padding-left: 14px;
        justify-content: flex-start;
        // font-weight: 600;
        .text-button-inner {
          color: var(--danger-color);
        }
      }
    }
    .list-container {
      ul.tree-display > .tree-display {
        margin-left: 0;
      }
      .tree-display {
        margin-top: 4px !important;
      }
      .documents-section,
      .characters-section,
      .worldbuilding-section,
      .chats-section {
        padding: 0px 16px;
      }
      .chats-section .document-list-item {
        padding: 6px 6px;
        svg.document-icon {
          width: 24px !important;
          margin-right: 6px;
        }
      }
      box-sizing: border-box;
      padding: 0px 16px;
      width: auto;
      flex-grow: 1;
      flex-shrink: 1;
      min-height: 40px;
      max-height: 100%;
      overflow-y: auto;
      overflow-x: hidden;
      &.trashed {
        padding: 0px;
      }

      &.styled-scrollbars {
        scrollbar-color: var(--scrollbar-thumb) var(--scrollbar-track);

        &::-webkit-scrollbar {
          width: 8px;
        }
        &::-webkit-scrollbar-track {
          background: var(--scrollbar-track);
        }
        &::-webkit-scrollbar-thumb {
          background-color: var(--scrollbar-thumb);
          border-radius: 5px;
        }
        &::-webkit-scrollbar-thumb:hover {
          background-color: var(--scrollbar-thumb-hover);
        }
      }

      ul {
        box-sizing: border-box;
        /* margin-left: 0px; */

        .tree-display-folder,
        .tree-display-document {
          margin-top: 2px;
        }

        .tree-display-document {
          border-radius: 5px;
        }

        /* Rows come from ProjectDocumentTreeChrome, which owns row layout, the
           folder/chevron swap, and icon colors. Only the action menu and
           document label we inject through its render slots are styled here.
           Drop feedback lives on the row rather than the full-width droppable
           wrapper so it lines up with the row's nesting indent. */
        .project-tree-folder-row,
        .project-tree-document-row {
          /* The row carries tabIndex, so the app focus ring belongs here. The
             wrappers around it are plain divs and never match :focus-visible. */
          &:focus-visible {
            ${focusOutline}
          }

          &.is-drop-target {
            background: ${themeVariables.listElementHover};
            outline: 2px solid var(--primary-cta);
          }

          /* Inline rename form, rendered in place of the row label. */
          form {
            flex: 1;
            min-width: 0;
            display: flex;
            align-items: center;

            input {
              flex: 1;
              min-width: 0;
              box-sizing: border-box;
              height: 34px;
              margin: 0 4px 0 0;
              padding: 6px 0 6px 6px;
              border: 2px solid var(--primary-cta);
              border-radius: 4px;
              background: var(--document-background);
              color: var(--grey-0);
              font-family: Inter;
              font-size: 16px;
              font-weight: 500;
              outline: 0;
            }
          }

          .item-menu {
            display: flex;
            align-items: center;
            align-self: center;
            flex-shrink: 0;
            box-sizing: border-box;
            height: 32px;
            margin: 0;
            border: none;
            background: transparent;
            line-height: 0;
            cursor: pointer;
            ${focusVisibleInset}

            .icon-wrapper {
              height: 24px;
              width: 24px;
              border-radius: 5px;
              background-color: transparent;

              svg {
                display: block;
                width: 18px;
                height: 18px;

                circle {
                  fill: var(--grey-1);
                }
              }

              &:hover {
                background-color: ${themeVariables.listElementHover};
              }
            }
          }

          &.is-selected,
          &.is-active {
            .item-menu {
              svg circle {
                fill: ${themeVariables.selectedElementText};
              }

              &:focus-visible {
                outline-color: ${themeVariables.selectedElementText};
              }
            }
          }
        }

        .project-tree-document-row {
          .document-label {
            flex: 1;
            min-width: 0;
            display: flex;
            flex-wrap: nowrap;
            align-items: center;
            justify-content: flex-start;
            overflow: hidden;
            border: none;
            background: none;
            padding: 0;
            text-align: left;
            text-decoration: none;
            cursor: pointer;
            font-family: inherit;
            font-size: 16px;
            line-height: 20px;
            font-weight: 500;
            color: var(--grey-0);

            /* Color the text element itself. Ancestor anchor rules elsewhere in
               the app outrank this selector, so an inherited color on the
               wrapping link would lose and leave the title unreadable. */
            pre {
              margin: 0;
              flex: 1;
              min-width: 0;
              white-space: nowrap;
              overflow: hidden;
              text-overflow: ellipsis;
              font: inherit;
              color: var(--grey-0);
            }

            &.untitled pre {
              color: var(--grey-1);
            }
          }

          &.is-selected .document-label pre,
          &.is-active .document-label pre {
            color: ${themeVariables.selectedElementText};
          }
        }

        .document-list-item {
          position: relative;
          display: flex;
          align-items: center;
          justify-content: space-between;
          border-radius: 5px;
          cursor: pointer;

          svg {
            flex-shrink: 0;
            path {
              fill: var(--grey-0);
            }
          }
        }

        .folder-list-item-editing {
          flex: 1;
          display: flex;
          flex-wrap: nowrap;
          align-items: center;
          justify-content: flex-start;

          svg {
            width: 28px;
            height: 28px;
            margin-left: -4px;
            path {
              fill: var(--grey-0);
            }
          }

          form {
            flex: 1;
            display: flex;

            input {
              flex: 1;
            }
          }
        }

        .document-list-item-editing {
          flex: 1;
          min-width: 0;
          display: flex;
          flex-wrap: nowrap;
          align-items: center;
          justify-content: flex-start;

          svg {
            width: 38px;
            height: auto;
            flex-shrink: 0;
            path {
              fill: var(--grey-0);
            }
          }

          form {
            flex: 1;
            min-width: 0;
            display: flex;

            input {
              flex: 1;
              min-width: 0;
            }
          }
        }

        .document-list-item {
          border-radius: 5px;
          padding: 0px 4px 0px 0px;
          transition: background-color 0.2s;
          &:focus-visible {
            ${focusOutline}
          }

          .item-menu {
            box-sizing: border-box;
            display: flex;
            align-items: center;
            height: 32px;
            width: 0;
            padding: 0 !important;
            overflow: hidden;
            background-color: transparent;
            border-radius: 3px;
            ${focusVisibleInset}

            .icon-wrapper {
              height: 24px;
              width: 24px;
              border-radius: 5px !important;
              svg {
                opacity: 0;
                width: 18px;
                height: 18px;
              }
              &:hover {
                background-color: ${themeVariables.listElementHover};
              }
            }

            svg {
              circle {
                fill: var(--grey-1);
              }
            }

            &[data-state="open"] {
              width: auto;
              padding: 0 4px !important;

              .icon-wrapper svg {
                opacity: 1;
              }
            }
          }

          &.nested {
            /* margin-left: 3px; */
          }

          &:hover {
            background: ${themeVariables.listElementHover};
            .item-menu {
              width: auto;
              padding: 0 4px !important;
              svg {
                opacity: 1;
              }
            }
          }

          &:focus-visible,
          &:has(:focus-visible) {
            .item-menu {
              width: auto;
              padding: 0 4px !important;
              svg {
                opacity: 1;
              }
            }
          }

          &.active,
          &.selected {
            background: ${themeVariables.selectedElementBackground};

            svg.document-icon {
              path {
                fill: ${themeVariables.selectedElementText} !important;
              }
            }

            pre {
              color: ${themeVariables.selectedElementText};
            }

            .item-menu:focus-visible {
              outline-color: ${themeVariables.selectedElementText};
            }

            &:hover {
              .item-menu {
                svg {
                  circle {
                    fill: ${themeVariables.selectedElementText};
                  }
                }
              }
            }
          }

          > div,
          a,
          button {
            border: none;
            background: none;
            padding: 0;
            text-align: left;
            color: inherit;
            font: inherit;
            cursor: pointer;
            outline: none;
            border-radius: 5px;

            &.document-label {
              display: flex;
              flex-wrap: nowrap;
              align-items: center;
              justify-content: flex-start;
              font-weight: 500;
              padding: 1px 1px 1px 0;
              line-height: 1.3;
              flex: 1;
              min-width: 0;
              overflow: hidden;

              .document-icon {
                padding-right: 3px;
              }

              &.empty-trash {
                color: var(--danger-color);
              }

              svg {
                width: 38px;
                height: auto;
                flex-shrink: 0;
                path {
                  fill: var(--grey-0);
                }
              }
              &.active {
                .document-icon {
                  path {
                    fill: var(--cta-text-color);
                  }
                }
              }
              pre {
                white-space: nowrap;
                overflow: hidden;
                text-overflow: ellipsis;
                flex: 1;
                min-width: 0;
              }
            }
          }

          &.trashed {
            &:has(.document-label:focus-visible) {
              ${focusOutline}
              outline-offset: 2px !important;
            }

            &.active .item-menu:focus-visible {
              outline-color: ${themeVariables.selectedElementText};
            }

            .document-label {
              max-width: calc(100% - 20px);

              svg {
                width: 30px;
              }
            }

            .item-menu {
              ${focusVisible}
            }
          }
        }
      }
    }

    .bottom-links {
      padding: 4px 16px;
      margin-top: auto;
    }

    .bottom-link {
      width: auto !important;

      .disabled {
        padding-right: 16px;
      }

      &.trash {
        width: fit-content !important;
        padding-right: 4px;
        margin-bottom: 5px;
      }
    }

    .sticky-container {
      margin-top: auto;
      position: relative;
    }
  }

  .top-menu {
    height: 46px;
    border-bottom: 2px solid var(--divider);
    font-family: Inter;
    position: relative;

    .collapse {
      position: absolute;
      width: 24px;
      height: 24px;
      right: 18px;
      top: 12px;
      display: flex;
      align-items: center;
      justify-content: center;
      cursor: pointer;
      svg {
        path {
          fill: var(--grey-2);
        }
      }
      div[role="button"] {
        ${focusVisibleInset}
      }
    }

    .title {
      font-size: 16px;
      font-weight: 500;
      line-height: 40px;
      padding: 4px 0 0 12px;
      width: calc(100% - 44px);
      overflow: hidden;
      white-space: nowrap;
      user-select: none;
      div[role="button"] {
        padding-left: 4px;
        ${focusVisibleInset}
        border-radius: 4px;
      }
      div {
        width: 92%;
        overflow: hidden;
        text-overflow: ellipsis;
        display: flex;
        align-items: center;
        whitespace: no-wrap;
        min-height: 40px;
      }
      svg {
        width: 22px;
        margin-right: 12px;
        flex-shrink: 0;
      }
      pre {
        color: var(--grey-0);
        display: inline;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
      }
      &:hover {
        cursor: text;
      }
      input {
        margin-top: 4px;
        font-weight: 500;
        padding: 0;
        width: 95%;
        border: 0;
        outline: 0;
        font-family: Inter;
        font-size: 16px;
        -webkit-box-sizing: border-box;
        -moz-box-sizing: border-box;
        box-sizing: border-box;
        background: var(--document-background);
        border: 2px solid var(--primary-cta);
        height: 30px;
        padding: 6px 0px 6px 4px;
        color: var(--grey-0);
        border-radius: 6px;
        margin-left: -2px;
      }
    }
  }

  .create-section {
    position: relative;
    display: flex;
    align-items: center;
  }
  .back {
    color: var(--grey-0);
    font-weight: 500;
    justify-content: flex-start;
    padding-left: 10px;
  }

  a.document-label {
    overflow: hidden;
    &:focus-visible {
      outline: none !important;
    }
  }

  .add {
    min-width: 90px;
    color: var(--grey-0);
    display: inline-flex;
    padding: 5px 6px 5px 4px;
    font-size: 16px;
    line-height: 21px;
    border: 2px solid var(--divider);
    border-radius: 8px;
    background: var(--menu-color-background);
    cursor: pointer;
    align-items: center;
    ${focusVisible}

    &.sw2 {
      box-sizing: border-box;
      border-radius: 8px;
      flex: 1 1 var(--leftbar-create-button-max);
      height: 38px;
      min-width: var(--leftbar-create-button-min);
      max-width: var(--leftbar-create-button-max);
      padding: 2px 14px 2px 10px;
      line-height: normal;
      justify-content: center;

      &:not(:first-child) {
        margin-left: 0;
      }

      &:not(.is-loading) {
        border: 2px solid var(--primary-cta-transparent);
      }

      &:hover {
        background-color: var(--primary-cta-transparent);
        border: 2px solid transparent;
      }
    }

    p {
      font-weight: 500;
    }
    svg {
      width: 20px;
      margin-right: 2px;
      path {
        fill: var(--grey-0);
        stroke: var(--grey-0);
      }
    }

    &.import {
      &:not(.is-loading) {
        background: var(--menu-color-background) !important;
        &:hover {
          background: var(--menu-color-active) !important;
          transition: none;
        }
      }
      &.sw2 {
        &:not(.is-loading) {
          border: 2px solid var(--primary-cta-transparent);
          border-radius: 8px;
        }

        &:not(.is-loading):hover {
          background-color: var(--primary-cta-transparent) !important;
          border: 2px solid transparent !important;
        }
      }
      svg {
        width: 14px;
        margin: 0 7px 0 5px;
        path {
          fill: none;
        }
      }
    }

    &:not(:first-child) {
      margin-left: 8px;
    }

    &:hover {
      background: var(--menu-color-active);
      border: 2px solid var(--menu-color-active);
    }

    &.disabled {
      opacity: 0.3;
      /* background: rgba(100, 100, 100, 0.05); */

      &:hover {
        background: var(--menu-color-hover);
        cursor: normal;
      }
    }
  }

  .list-container {
    width: 100%;
    position: relative;
    overflow-y: auto;
    scrollbar-color: var(--scrollbar-thumb) var(--scrollbar-track);

    &::-webkit-scrollbar {
      width: 8px;
    }
    &::-webkit-scrollbar-track {
      background: var(--scrollbar-track);
    }
    &::-webkit-scrollbar-thumb {
      background-color: var(--scrollbar-thumb);
      border-radius: 5px;
    }
    &::-webkit-scrollbar-thumb:hover {
      background-color: var(--scrollbar-thumb-hover);
    }

    div.disabled {
      display: block;
      padding: 8px 0 8px 16px;
      color: var(--grey-2);
      font-weight: 500;

      svg {
        position: relative;
        top: 2px;
        fill: var(--grey-0);
      }
    }
  }

  ul {
    li {
      font-size: 16px;
      position: relative;
      pre {
        color: var(--grey-0);
        overflow: hidden;
        text-overflow: ellipsis;
      }

      &.active:not(:hover) {
        background: var(--menu-color-hover);
      }

      & > a {
        transition: none;
        display: block;
        white-space: nowrap;
        padding: 8px;
        font-weight: 500;
        pre {
          color: var(--grey-0);
          font-weight: 500;
          min-height: 16px;
        }

        &.untitled {
          color: var(--grey-1);
        }

        /* &:hover {
          background: rgba(100, 100, 100, 0.05);
        } */
      }

      .item-menu {
        display: flex;
        visibility: hidden;
        position: absolute;
        top: 5px;
        bottom: 5px;
        padding: 0 6px;
        border-radius: 5px !important;
        justify-content: center;
        align-items: center;
        right: 5px;
        background-color: var(--menu-color-hover);
        &.active.sw2 {
          background-color: var(--primary-cta-hover);

          &:hover {
            background-color: var(--primary-cta-active);
          }
        }

        a {
          transition: none;
        }

        svg {
          width: 18px;
          path {
            stroke: var(--grey-0);
          }
        }

        &:hover {
          background-color: var(--menu-color-active);
          cursor: pointer;
        }

        a {
          transition: none;
          line-height: 0;
        }
      }

      &:hover,
      &:focus-within {
        & > a {
          background: var(--menu-color-hover);
        }

        &.no-highlight > a {
          background: none;
          pre {
            color: var(--grey-0);
          }
        }

        .item-menu {
          visibility: visible;
        }
      }

      input {
        width: calc(100% - 16px);
        border: 2px solid var(--primary-cta);
        background: var(--document-background);
        color: var(--grey-0);
        border-radius: 4px;
        margin-left: 0px;
        font-weight: 500;
        outline: 0;
        font-family: Inter;
        font-size: 16px;
        padding: 6px 0 6px 6px;
        height: 34px;
        -webkit-box-sizing: border-box;
        -moz-box-sizing: border-box;
        box-sizing: border-box;
      }
    }
  }

  .sticky-container {
    position: fixed;
    bottom: 0;
    display: block;
  }

  .bottom-link {
    margin: 0;
    /* height: 34px; */
    color: var(--grey-0);
    display: block;

    &.sticky {
      a {
        /* background: white; */
      }
    }

    bottom: 0;

    div.disabled {
      display: block;
      padding: 8px 0 8px 16px;
      color: var(--grey-2);
      font-weight: 500;

      svg {
        position: relative;
        top: 2px;
        fill: var(--grey-0);
      }
    }
  }

  .sw2-container {
    flex-shrink: 1;
  }
`;
